import React, { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, HelpCircle, Phone, MessageSquare, Loader2 } from "lucide-react";
import BUSINESS from "../config/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FAQPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/public/faq`)
      .then(r => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section data-testid="faq-page" className="mx-auto max-w-3xl px-5 sm:px-8 py-16">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 mb-3 text-electric text-xs tracking-[0.4em]">
          <HelpCircle size={14} /> FREQUENTLY ASKED QUESTIONS
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl mb-3">Everything you need to know</h1>
        <p className="text-white/60 text-sm max-w-xl mx-auto">
          Answers on our luxury fleet, rental requirements, chauffeur service, insurance, deposits, and policies.
          Still have a question? Our concierge team is available 24/7.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center text-white/50 text-sm py-16">
          <Loader2 size={16} className="animate-spin mr-2" /> Loading FAQ…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center text-white/50 text-sm py-16">
          No FAQ items yet. Check back soon.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item, i) => {
            const open = openIdx === i;
            return (
              <div
                key={item.id}
                data-testid={`faq-item-${i}`}
                className={`rounded-2xl border transition ${
                  open ? "border-electric/50 bg-electric/[0.04]" : "border-white/10 bg-white/[0.02] hover:border-white/25"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-start justify-between gap-4 text-left px-5 py-4"
                >
                  <span className={`font-serif text-lg leading-snug ${open ? "text-electric" : "text-white"}`}>
                    {item.question}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`shrink-0 mt-1 text-white/50 transition-transform ${open ? "rotate-180 text-electric" : ""}`}
                  />
                </button>
                {open && (
                  <div className="px-5 pb-5 pt-1 text-white/70 text-sm leading-relaxed whitespace-pre-wrap">
                    {item.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-14 rounded-3xl border border-electric/30 bg-electric/[0.05] p-8 text-center">
        <h2 className="font-serif text-2xl mb-2">Still have questions?</h2>
        <p className="text-white/60 text-sm mb-6">Our 24/7 concierge team is one call or text away.</p>
        <div className="flex flex-wrap justify-center gap-3">
          <a href={`tel:${BUSINESS.phoneTel}`} data-testid="faq-call"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-electric text-ink-900 font-semibold hover:brightness-110 transition">
            <Phone size={14} /> {BUSINESS.phone}
          </a>
          <a href="/support" data-testid="faq-support"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white hover:border-electric hover:text-electric transition">
            <MessageSquare size={14} /> Contact Concierge
          </a>
        </div>
      </div>
    </section>
  );
}
